import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { CheckboxRadio as Checkbox } from '@glu/form-components';
import { Loading } from '@glu/indicators-react';
import gluLocale from '@glu/locale';
import { LoadMore } from '@glu/grid-mobile-react';
import { CURRENCY } from 'constants.js';
import MobileGridContext from '../logic/MobileGridContext';
import useStyles from './MobileGrid.styles';

const MobileGrid = ({ children, onBulkActionSelectAll, pageData }) => {
  const { bulkActionSvg, leftAlign, rendererRoot, rightAlign, tableMain } = useStyles();
  const gridContext = React.useContext(MobileGridContext);
  const { data, isPaginating, isSelectAllChecked, page, pageSize, primaryColumns, totalRows } =
    gridContext;
  const visibleRowsSize = data.length;
  const bulkActionsEnabled = Boolean(gridContext.bulkActionState);
  const handleCheckboxSelect = useCallback(
    (_name, isChecked, isBlurEvent) => {
      if (isBlurEvent) return;
      onBulkActionSelectAll(isChecked);
    },
    [onBulkActionSelectAll]
  );

  return (
    <div className={rendererRoot} data-qa="mobilegrid">
      <table className={tableMain} role="grid">
        <thead>
          <tr>
            {bulkActionsEnabled && (
              <th className={bulkActionSvg}>
                <Checkbox
                  htmlId="bulkSelectAll"
                  name="bulkSelectAll"
                  checked={isSelectAllChecked}
                  onChange={handleCheckboxSelect}
                  labelText={gluLocale.get('selectAll')}
                  screenReaderLabel
                />
              </th>
            )}
            {/* Need to have this extra th to match the four columns in the table rows */}
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <th />
            {primaryColumns.map((col) => {
              // If there is a condition to check whether this should show or not
              const shouldShow = col.condition
                ? col.condition({
                    data: null,
                    gridContext,
                    conditions: gridContext.columnConditions
                  })
                : true;
              const shouldRightAlign = col.variant === CURRENCY;
              if (!shouldShow) return null;
              return (
                <th className={shouldRightAlign ? rightAlign : leftAlign} key={`th_${col.field}`}>
                  {col.headerDisplay ? col.headerDisplay({ column: col }) : col.headerName}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
      <Loading isLoading={isPaginating}>
        <LoadMore
          onLoadMore={pageData}
          page={page}
          pageSize={pageSize}
          totalRows={totalRows}
          visibleRowsSize={visibleRowsSize}
        />
      </Loading>
    </div>
  );
};

MobileGrid.propTypes = {
  children: PropTypes.node.isRequired,
  onBulkActionSelectAll: PropTypes.func,
  pageData: PropTypes.func.isRequired
};

MobileGrid.defaultProps = {
  onBulkActionSelectAll: undefined
};

export default MobileGrid;
